import React, { useState, useEffect } from 'react';
import { canManageClientOperations } from '../utils/clientOperationsAccess';
import {
  Phone,
  Printer,
  Mail,
  FileEdit,
  FilePlus,
  User,
  X,
  Send,
  CheckCircle2,
  AlertCircle,
  Search,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Volume2,
  VolumeX,
  FileText,
  Wrench,
  ArrowRight
} from 'lucide-react';
import { Client, Provider, UserRole } from '../types';
import { playPulseSeal, getPulseSoundMode, togglePulseSound, PulseSoundMode } from '../utils/pulseAudio';
import { formatAuthNoticeDocumentName } from '../utils/authNoticeNaming';

function isSingleValidEmail(value: string): boolean {
  const clean = value.trim().toLowerCase();
  if (!clean || clean.includes(' ') || clean.includes('\t') || clean.includes('\n') || clean.includes(',')) {
    return false;
  }
  if (!clean.includes('@')) return false;
  const parts = clean.split('@');
  if (parts.length !== 2 || !parts[0] || !parts[1]) return false;
  if (!parts[1].includes('.')) return false;
  return !parts[1].split('.').some((part) => part.length === 0);
}

function isPlaceholderScEmail(value?: string | null): boolean {
  const clean = String(value || '').trim().toLowerCase();
  if (!clean) return true;
  return (
    clean.includes('support_coord@ddd.az.gov') ||
    clean.includes('example.com') ||
    clean.includes('placeholder') ||
    clean.startsWith('dummy') ||
    clean.startsWith('none@') ||
    clean.startsWith('fake@')
  );
}

function verifiedScEmail(value?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw || isPlaceholderScEmail(raw) || !isSingleValidEmail(raw)) return '';
  return raw;
}

function usableAuthValue(...candidates: Array<string | null | undefined>): string {
  for (const candidate of candidates) {
    const value = String(candidate || '').trim();
    if (!value) continue;
    if (/^(on file|not on file|upcoming)$/i.test(value)) continue;
    return value;
  }
  return '';
}

function buildSevenDayAuthEmail(clientFullName: string, expirationDate: string, authorizationNumber: string) {
  const expireOn = expirationDate ? ` on ${expirationDate}` : '';
  const contextLines = ['• Service: Speech Therapy (92507 / ST)'];
  if (authorizationNumber) contextLines.push(`• Authorization #: ${authorizationNumber}`);
  if (expirationDate) contextLines.push(`• Expiration Date: ${expirationDate}`);
  return {
    subject: 'Allegiant Therapy STA Renewal Request',
    body: [
      'Hello Support Coordinator,',
      '',
      `This is a brief notice that the authorization for speech therapy services for ${clientFullName} is scheduled to expire in 7 days${expireOn}.`,
      '',
      'Current Authorization Context:',
      ...contextLines,
      '',
      'Please let us know if an updated authorization has been issued or if our office needs to submit any updated clinical documentation to ensure continuous services.',
      '',
      'Thank you,',
      'Allegiant Therapy',
      'info@allegianttherapy.com'
    ].join('\n')
  };
}

function buildThreeDayAuthEmail(clientFullName: string, expirationDate: string) {
  const expireOn = expirationDate ? ` on ${expirationDate}` : '';
  return {
    subject: 'Allegiant Therapy STA Renewal Request – Urgent Follow-Up',
    body: [
      'Hello Support Coordinator,',
      '',
      `This is an urgent follow-up regarding speech therapy services for ${clientFullName}.`,
      '',
      `The current authorization expires in 3 days${expireOn}.`,
      '',
      'Please provide an updated authorization or status update as soon as possible to prevent any interruption to the client\'s scheduled therapy sessions.',
      '',
      'Thank you for your prompt assistance,',
      'Allegiant Therapy',
      'info@allegianttherapy.com'
    ].join('\n')
  };
}

function buildPocSupportCoordinatorEmail(clientFullName: string) {
  return {
    subject: 'Allegiant Therapy Physician-Signed Plan of Care',
    body: [
      'Hello Support Coordinator,',
      '',
      `Please find attached the approved, physician-signed Speech Therapy Plan of Care for ${clientFullName}.`,
      '',
      'This Plan of Care has been reviewed and signed by the ordering physician and is on file with our clinic for your records and authorization processing.',
      '',
      'Please let us know if you need any additional information.',
      '',
      'Best regards,',
      'Allegiant Therapy',
      'info@allegianttherapy.com'
    ].join('\n')
  };
}

interface UniversalToolsBarProps {
  userRole: UserRole;
  selectedClient?: Client | null;
  selectedProvider?: Provider | null;
  allClients?: Client[];
  onSelectClient?: (clientId: string) => void;
  onOpenCall?: (client?: Client | null) => void;
  onOpenFax?: (client?: Client | null) => void;
  onCreateClinicalDoc?: (clientId: string) => void;
  onOpenAIWithContext?: (prompt: string, contextId?: string) => void;
  onOpenAI?: (prompt: string) => void;
  onNavigateToTab?: (tab: string) => void;
  onRefreshData?: () => void;
  externalEmailRequest?: {
    clientId: string;
    templateKey: string;
    authContext?: any;
    timestamp: number;
  } | null;
  isMobileSheetOpen?: boolean;
  onCloseMobileSheet?: () => void;
}

export const UniversalToolsBar: React.FC<UniversalToolsBarProps> = ({
  userRole,
  selectedClient,
  selectedProvider,
  allClients = [],
  onSelectClient,
  onOpenCall,
  onOpenFax,
  onCreateClinicalDoc,
  onOpenAIWithContext,
  onOpenAI,
  onNavigateToTab,
  onRefreshData,
  externalEmailRequest,
  isMobileSheetOpen,
  onCloseMobileSheet
}) => {
  const [mobileSheetOpenLocal, setMobileSheetOpenLocal] = useState(false);

  useEffect(() => {
    const handleOpenMobileSheet = () => {
      setMobileSheetOpenLocal(true);
    };
    window.addEventListener('pulse_open_mobile_universal_tools', handleOpenMobileSheet);
    return () => {
      window.removeEventListener('pulse_open_mobile_universal_tools', handleOpenMobileSheet);
    };
  }, []);

  const isMobileOpen = isMobileSheetOpen !== undefined ? isMobileSheetOpen : mobileSheetOpenLocal;

  const handleCloseMobileSheet = () => {
    setMobileSheetOpenLocal(false);
    if (onCloseMobileSheet) {
      onCloseMobileSheet();
    }
  };

  const [showDraftEmailModal, setShowDraftEmailModal] = useState(false);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [emailDraft, setEmailDraft] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    attachmentDocId: '',
    templateKey: '',
    authId: '',
    noticeType: '' as '7_DAY' | '3_DAY' | ''
  });
  const [clientDocs, setClientDocs] = useState<any[]>([]);
  const [clientAuths, setClientAuths] = useState<any[]>([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSendError, setEmailSendError] = useState<string | null>(null);
  const [quickNote, setQuickNote] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);
  const [savingNote, setSavingNote] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState<string | null>(null);

  // Universal action flow: client selection picker for tools triggered without client
  type PendingTool = 'CALL' | 'FAX' | 'EMAIL' | 'NOTE' | 'CLINICAL_DOC' | null;
  const [pendingTool, setPendingTool] = useState<PendingTool>(null);
  const [showClientPickerModal, setShowClientPickerModal] = useState(false);
  const [mobileWorkflowOrigin, setMobileWorkflowOrigin] = useState<'HUB' | 'PICKER' | null>(null);
  const [clientPickerSearch, setClientPickerSearch] = useState('');
  const [pickerClients, setPickerClients] = useState<Client[]>(allClients || []);
  const [activeOperativeClient, setActiveOperativeClient] = useState<Client | null>(null);
  const [soundMode, setSoundMode] = useState<PulseSoundMode>(() => getPulseSoundMode());

  useEffect(() => {
    const handleSoundChange = (e: Event) => {
      const custom = e as CustomEvent<{ mode: PulseSoundMode }>;
      if (custom.detail?.mode) {
        setSoundMode(custom.detail.mode);
      } else {
        setSoundMode(getPulseSoundMode());
      }
    };
    window.addEventListener('pulse_sound_mode_changed', handleSoundChange);
    return () => {
      window.removeEventListener('pulse_sound_mode_changed', handleSoundChange);
    };
  }, []);

  const handleToggleSound = () => {
    const next = togglePulseSound();
    setSoundMode(next);
  };

  const [loadingClients, setLoadingClients] = useState(false);
  const clientRosterKey = allClients.map((client) => client.id).join('\n');

  // Load the roster once. A new empty array each render is not a roster change.
  useEffect(() => {
    if (allClients.length > 0) {
      setPickerClients(allClients);
      return;
    }
    let isMounted = true;
    setLoadingClients(true);
    fetch('/api/clients')
      .then(res => res.json())
      .then(data => {
        if (isMounted && Array.isArray(data)) {
          setPickerClients(data);
        }
      })
      .catch(err => {
        console.warn('[UniversalToolsBar] Could not load client roster:', err);
      })
      .finally(() => {
        if (isMounted) setLoadingClients(false);
      });
    return () => {
      isMounted = false;
    };
  }, [clientRosterKey]);

  // Fallback loader if sheet or picker opened while clients state is empty
  useEffect(() => {
    if ((showClientPickerModal || isMobileOpen) && pickerClients.length === 0 && !loadingClients) {
      fetch('/api/clients')
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) setPickerClients(data);
        })
        .catch(() => {});
    }
  }, [showClientPickerModal, isMobileOpen, pickerClients.length, loadingClients]);

  const filteredPickerClients = pickerClients.filter((c) => {
    const q = clientPickerSearch.toLowerCase().trim();
    if (!q) return true;
    const firstLast = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
    const lastFirst = `${c.last_name || ''} ${c.first_name || ''}`.toLowerCase();
    const clientId = (c.id || '').toLowerCase();
    const ahcccs = (c.ahcccs_id || '').toLowerCase();
    const policy = (c.policy_id || '').toLowerCase();
    return firstLast.includes(q) || lastFirst.includes(q) || clientId.includes(q) || ahcccs.includes(q) || policy.includes(q);
  });

  const releaseActionClient = () => {
    setActiveOperativeClient(null);
    setClientPickerSearch('');
  };

  const clientForDockAction = (): Client | null => selectedClient || null;

  const currentOperativeTarget = activeOperativeClient || selectedClient;

  const activeTargetName = currentOperativeTarget
    ? `${currentOperativeTarget.last_name}, ${currentOperativeTarget.first_name} [${currentOperativeTarget.id}]${currentOperativeTarget.ahcccs_id ? ` (AHCCCS: ${currentOperativeTarget.ahcccs_id})` : ''}`
    : selectedProvider
    ? `${selectedProvider.name} (${selectedProvider.role})`
    : null;

  const isClinicalStaff = userRole === 'ASSISTANT' || userRole === 'SUPERVISOR';
  const canUseUniversalCall = canManageClientOperations(userRole);

  const showNotification = (msg: string) => {
    setFeedbackMsg(msg);
    setTimeout(() => setFeedbackMsg(null), 4000);
  };

  const handleTriggerCall = () => {
    if (!canUseUniversalCall) return;
    onOpenCall?.(null);
  };

  const handleTriggerFax = () => {
    if (!canUseUniversalCall) return;
    onOpenFax?.(null);
  };

  useEffect(() => {
    if (!externalEmailRequest) return;
    const { clientId, templateKey, authContext } = externalEmailRequest;

    const trigger = async () => {
      let client = selectedClient;
      if (!client || client.id !== clientId) {
        try {
          const res = await fetch(`/api/clients/${clientId}`);
          if (res.ok) {
            client = await res.json();
          }
        } catch (e) {
          console.error('Error fetching client for email trigger:', e);
        }
      }

      if (!client) return;
      setActiveOperativeClient(client);

      const clientFullName = `${client.first_name} ${client.last_name}`;
      const scEmail = verifiedScEmail(client.support_coordinator_email);
      const expirationDate = usableAuthValue(authContext?.end_date, client.poc_end_date);
      const authorizationNumber = usableAuthValue(authContext?.auth_number, authContext?.authorization_number);

      let to = scEmail;
      let subject = '';
      let body = '';

      if (templateKey === 'auth_7day') {
        const built = buildSevenDayAuthEmail(clientFullName, expirationDate, authorizationNumber);
        subject = built.subject;
        body = built.body;
      } else if (templateKey === 'auth_3day') {
        const built = buildThreeDayAuthEmail(clientFullName, expirationDate);
        subject = built.subject;
        body = built.body;
      }

      const resolvedNoticeType = templateKey === 'auth_7day' ? '7_DAY' : templateKey === 'auth_3day' ? '3_DAY' : '';
      const resolvedAuthId = authContext?.id || '';

      setEmailDraft({
        to,
        cc: '',
        bcc: '',
        subject,
        body,
        attachmentDocId: '',
        templateKey,
        authId: resolvedAuthId,
        noticeType: resolvedNoticeType
      });
      setEmailSendError(null);
      setShowDraftEmailModal(true);
    };

    trigger();
  }, [externalEmailRequest?.timestamp]);

  const openEmailDraftForClient = async (client: Client) => {
    setActiveOperativeClient(client);
    setEmailDraft({
      to: client.guardian_email || client.email || '',
      cc: '',
      bcc: '',
      subject: `Allegiant Therapy Update - ${client.first_name} ${client.last_name}`,
      body: `Hello,\n\nThis is regarding speech therapy services for ${client.first_name} ${client.last_name}.\n\nPlease let us know if you have any questions.\n\nBest regards,\nAllegiant Therapy\ninfo@allegianttherapy.com`,
      attachmentDocId: '',
      templateKey: '',
      authId: '',
      noticeType: ''
    });
    setEmailSendError(null);
    setShowDraftEmailModal(true);

    // Fetch authentic completed documents and authorizations for this client
    try {
      const [docsRes, clientRes] = await Promise.all([
        fetch(`/api/clinical/docs?client_id=${client.id}`),
        fetch(`/api/clients/${client.id}`)
      ]);
      if (docsRes.ok) {
        const docs = await docsRes.json();
        setClientDocs(Array.isArray(docs) ? docs : []);
      }
      if (clientRes.ok) {
        const clientData = await clientRes.json();
        if (clientData.authorizations && Array.isArray(clientData.authorizations)) {
          setClientAuths(clientData.authorizations);
        }
      }
    } catch {
      setClientDocs([]);
      setClientAuths([]);
    }
  };

  const handleTriggerEmail = async () => {
    const client = clientForDockAction();
    if (client) {
      openEmailDraftForClient(client);
    } else {
      setPendingTool('EMAIL');
      setShowClientPickerModal(true);
    }
  };

  const applyEmailTemplate = (templateKey: string) => {
    const client = activeOperativeClient || selectedClient;
    if (!client) return;
    const clientFullName = `${client.first_name} ${client.last_name}`;
    const dob = client.dob || 'On File';
    const ahcccsId = client.ahcccs_id || 'Not on file';
    const guardian = client.guardian_name || 'Parent / Guardian';
    const scName = client.support_coordinator_name || 'Support Coordinator';
    const scEmail = verifiedScEmail(client.support_coordinator_email);
    const guardianEmail = client.guardian_email || client.email || '';
    const physician = client.physician_name || 'Primary Care Provider';

    const activeAuth = clientAuths.find((a: any) => a.status === 'ACTIVE') || clientAuths[0];
    const expirationDate = usableAuthValue(activeAuth?.end_date, client.poc_end_date);
    const authorizationNumber = usableAuthValue(activeAuth?.authorization_number);

    let to = emailDraft.to;
    let subject = emailDraft.subject;
    let body = emailDraft.body;
    let attachmentDocId = emailDraft.attachmentDocId;
    let authId = emailDraft.authId;
    let noticeType: '7_DAY' | '3_DAY' | '' = '';

    switch (templateKey) {
      case 'auth_7day': {
        to = scEmail;
        authId = activeAuth?.id || authId;
        noticeType = '7_DAY';
        const built = buildSevenDayAuthEmail(clientFullName, expirationDate, authorizationNumber);
        subject = built.subject;
        body = built.body;
        break;
      }

      case 'auth_3day': {
        to = scEmail;
        authId = activeAuth?.id || authId;
        noticeType = '3_DAY';
        const built = buildThreeDayAuthEmail(clientFullName, expirationDate);
        subject = built.subject;
        body = built.body;
        break;
      }

      case 'poc_sc': {
        to = scEmail;
        const built = buildPocSupportCoordinatorEmail(clientFullName);
        subject = built.subject;
        body = built.body;

        // Auto-select physician-signed POC document attachment if available
        const signedPoc = clientDocs.find((d: any) =>
          d.doc_type === 'PHYSICIAN_SIGNED_POC' ||
          (d.doc_type === 'POC' && (d.physician_signature || d.title?.toLowerCase().includes('physician'))) ||
          d.title?.toLowerCase().includes('physician-signed') ||
          d.title?.toLowerCase().includes('signed poc')
        ) || clientDocs.find((d: any) => d.doc_type === 'POC');

        if (signedPoc) {
          attachmentDocId = signedPoc.id;
        }
        break;
      }

      // 4. Welcome to Allegiant Therapy
      case 'welcome_allegiant':
        to = guardianEmail;
        subject = 'Welcome to Allegiant Therapy';
        body = `Dear ${guardian},\n\nWelcome to Allegiant Therapy! We are excited to get started working with ${client.first_name}.\n\nOur team looks forward to providing speech therapy care tailored to your family's needs. If you have any questions as we begin, please reach out to our office at any time.\n\nScheduling & Cancellations: If you need to cancel a scheduled therapy visit, please notify your therapist or our office at least two (2) hours before the scheduled visit whenever possible. A no-call/no-show occurs when the therapist arrives for the scheduled visit and the family has not provided notice that the visit needs to be cancelled.\n\nProviding notice helps prevent unnecessary travel and allows us to maintain reliable scheduling for your family and therapist.\n\nWarm regards,\nAllegiant Therapy\ninfo@allegianttherapy.com`;
        attachmentDocId = '';
        break;

      case 'general_client':
        to = guardianEmail;
        subject = `Allegiant Speech Therapy Update - ${clientFullName}`;
        body = `Dear ${guardian},\n\nThis is regarding speech therapy services for ${clientFullName}.\n\nPlease let us know if you have any questions or scheduling updates.\n\nWarm regards,\nAllegiant Therapy\ninfo@allegianttherapy.com`;
        break;

      case 'support_coordinator':
        to = scEmail;
        subject = `Speech Therapy Status Update - ${clientFullName} (${ahcccsId})`;
        body = `Hello ${scName},\n\nWe are providing a routine service and documentation update for ${clientFullName} (DOB: ${dob}).\n\nPlease let us know if additional coordination or records are needed for their DDD service plan.\n\nBest regards,\nAllegiant Therapy\ninfo@allegianttherapy.com`;
        break;

      case 'physician_pcp':
        subject = `Pediatric Speech Therapy Plan of Care / Records Request - ${clientFullName}`;
        body = `Attention: Dr. ${physician} / Medical Records Department\n\nRe: ${clientFullName} (DOB: ${dob})\n\nAttached is the latest clinical documentation and Plan of Care for your patient. Please review and return with physician signature where required.\n\nThank you,\nAllegiant Therapy\ninfo@allegianttherapy.com`;
        break;

      case 'poc_signature':
        to = guardianEmail;
        subject = `Action Required: Signature Request for Speech Therapy Plan of Care - ${clientFullName}`;
        body = `Dear ${guardian},\n\nA new Plan of Care has been completed for ${clientFullName}${client.poc_end_date ? ` (Coverage period ending ${client.poc_end_date})` : ''}.\n\nPlease review the attached document and provide your signature so therapy services can continue without interruption.\n\nBest regards,\nAllegiant Therapy\ninfo@allegianttherapy.com`;
        break;

      case 'doc_delivery':
        to = guardianEmail;
        subject = `Clinical Documentation Delivery - ${clientFullName}`;
        body = `Hello,\n\nPlease find attached the clinical documentation for ${clientFullName}.\n\nIf you have any questions or require an amended copy, please contact Allegiant Therapy.\n\nBest regards,\nAllegiant Therapy\ninfo@allegianttherapy.com`;
        break;

      default:
        break;
    }

    setEmailDraft(prev => ({
      ...prev,
      templateKey,
      to,
      subject,
      body,
      attachmentDocId,
      authId,
      noticeType
    }));
  };

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    const to = emailDraft.to.trim();
    const subject = emailDraft.subject.trim();
    const body = emailDraft.body.trim();
    const cc = emailDraft.cc.trim();
    const bcc = emailDraft.bcc.trim();

    if (!to) {
      setEmailSendError('Enter one recipient email address before sending.');
      return;
    }
    if (!isSingleValidEmail(to)) {
      setEmailSendError('Enter one valid email address. Multiple recipients are not supported.');
      return;
    }
    if (!subject) {
      setEmailSendError('Subject is required.');
      return;
    }
    if (!body) {
      setEmailSendError('Message body is required.');
      return;
    }

    setEmailSendError(null);
    try {
      setSendingEmail(true);
      const targetClient = activeOperativeClient || selectedClient;
      const selectedDoc = clientDocs.find(d => d.id === emailDraft.attachmentDocId);
      const noticeKind = emailDraft.noticeType === '7_DAY' ? '7-Day' : emailDraft.noticeType === '3_DAY' ? '3-Day' : null;
      const noticeAttachmentName = noticeKind && targetClient
        ? `${formatAuthNoticeDocumentName(targetClient.first_name, targetClient.last_name, noticeKind)}.pdf`
        : undefined;
      const res = await fetch('/api/communications/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: targetClient?.id,
          to_email: to,
          cc: cc || undefined,
          bcc: bcc || undefined,
          subject,
          body,
          attachment_document_id: emailDraft.attachmentDocId || undefined,
          attachment_name: noticeAttachmentName
            || (selectedDoc ? `${selectedDoc.doc_type} - ${selectedDoc.title || selectedDoc.id}` : undefined),
          authorization_id: emailDraft.authId || undefined,
          notice_type: emailDraft.noticeType || undefined
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        playPulseSeal();
        showNotification(data.message || 'Email successfully transmitted via carrier transport.');
        setShowDraftEmailModal(false);
        releaseActionClient();
        if (onRefreshData) onRefreshData();
      } else {
        const blocked = `Email delivery blocked: ${data.failureReason || data.error || 'Real transport not configured.'}`;
        setEmailSendError(blocked);
        showNotification(blocked);
      }
    } catch (err: any) {
      const transportError = `Email transport error: ${err.message || 'Network error'}`;
      setEmailSendError(transportError);
      showNotification(transportError);
    } finally {
      setSendingEmail(false);
    }
  };

  const openAddNoteForClient = (client: Client) => {
    setActiveOperativeClient(client);
    setQuickNote('');
    setNoteError(null);
    setShowAddNoteModal(true);
  };

  const handleTriggerAddNote = () => {
    const client = clientForDockAction();
    if (client || selectedProvider) {
      if (client) setActiveOperativeClient(client);
      setQuickNote('');
      setNoteError(null);
      setShowAddNoteModal(true);
    } else {
      setPendingTool('NOTE');
      setShowClientPickerModal(true);
    }
  };

  const handleSaveNote = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = quickNote.trim();
    if (!trimmed) return;
    setNoteError(null);
    setSavingNote(true);

    try {
      const targetClient = activeOperativeClient || selectedClient;
      if (targetClient) {
        const res = await fetch(`/api/clients/${targetClient.id}/notes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            note: trimmed,
            category: 'GENERAL',
            source: 'UNIVERSAL_NOTE'
          })
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({ error: `Request failed with status ${res.status}` }));
          throw new Error(errData.error || `Server returned HTTP ${res.status}`);
        }
      } else {
        throw new Error('Please select an active client to attach this operational note.');
      }

      playPulseSeal();
      showNotification('Note added to record successfully.');
      setQuickNote('');
      setShowAddNoteModal(false);
      releaseActionClient();
      if (onRefreshData) onRefreshData();
    } catch (err: any) {
      setNoteError(err.message || 'Failed to save note. Please try again.');
    } finally {
      setSavingNote(false);
    }
  };

  const handleTriggerClinicalDoc = () => {
    const client = clientForDockAction();
    if (client) {
      if (onCreateClinicalDoc) onCreateClinicalDoc(client.id);
      else {
        showNotification(`Opening clinical documentation for ${client.first_name} ${client.last_name}`);
      }
    } else {
      setPendingTool('CLINICAL_DOC');
      setShowClientPickerModal(true);
    }
  };

  const handleSelectClientForPendingTool = (client: Client) => {
    const tool = pendingTool;
    setShowClientPickerModal(false);
    setPendingTool(null);
    setClientPickerSearch('');
    setMobileWorkflowOrigin('PICKER');

    if (tool === 'CALL') {
      if (onOpenCall) onOpenCall(client);
      releaseActionClient();
    } else if (tool === 'FAX') {
      if (onOpenFax) onOpenFax(client);
      releaseActionClient();
    } else if (tool === 'EMAIL') {
      openEmailDraftForClient(client);
    } else if (tool === 'NOTE') {
      openAddNoteForClient(client);
    } else if (tool === 'CLINICAL_DOC') {
      if (onCreateClinicalDoc) onCreateClinicalDoc(client.id);
      else if (onNavigateToTab) onNavigateToTab('clinical');
      releaseActionClient();
    }
  };

  const handleBackFromEmail = () => {
    setShowDraftEmailModal(false);
    if (mobileWorkflowOrigin === 'PICKER') {
      setShowClientPickerModal(true);
    } else {
      setMobileSheetOpenLocal(true);
      window.dispatchEvent(new CustomEvent('pulse_open_mobile_universal_tools'));
    }
  };

  const handleCloseEmail = () => {
    setShowDraftEmailModal(false);
    setEmailSendError(null);
    setMobileWorkflowOrigin(null);
    releaseActionClient();
    handleCloseMobileSheet();
  };

  const handleBackFromNote = () => {
    setShowAddNoteModal(false);
    if (mobileWorkflowOrigin === 'PICKER') {
      setShowClientPickerModal(true);
    } else {
      setMobileSheetOpenLocal(true);
      window.dispatchEvent(new CustomEvent('pulse_open_mobile_universal_tools'));
    }
  };

  const handleCloseNote = () => {
    setShowAddNoteModal(false);
    setMobileWorkflowOrigin(null);
    releaseActionClient();
    handleCloseMobileSheet();
  };

  const handleBackFromPicker = () => {
    setShowClientPickerModal(false);
    setPendingTool(null);
    setClientPickerSearch('');
    setMobileWorkflowOrigin(null);
    setMobileSheetOpenLocal(true);
    window.dispatchEvent(new CustomEvent('pulse_open_mobile_universal_tools'));
  };

  const handleClosePicker = () => {
    setShowClientPickerModal(false);
    setPendingTool(null);
    setClientPickerSearch('');
    setMobileWorkflowOrigin(null);
    handleCloseMobileSheet();
  };

  const handleTriggerAI = () => {
    const prompt = selectedClient
      ? `Summarize active authorizations, speech POC goals, and completed services for client ${selectedClient.first_name} ${selectedClient.last_name} (${selectedClient.id}).`
      : selectedProvider
      ? `Summarize caseload, supervisor relationship, and completed services for provider ${selectedProvider.name} (${selectedProvider.id}).`
      : 'How can Allegiant AI assist with therapy caseload, authorizations, or completed services?';

    if (onOpenAI) {
      onOpenAI(prompt);
    } else if (onOpenAIWithContext) {
      onOpenAIWithContext(prompt, selectedClient?.id || selectedProvider?.id);
    }
  };

  const isDesktopFixedFooterRole =
    userRole === 'OWNER' ||
    userRole === 'ADMIN' ||
    userRole === 'REFERRAL_COORDINATOR' ||
    userRole === 'OFFICE';

  return (
    <>
      {/* Docked Universal Tools Area */}
      <div
        className={`bg-white border-t border-slate-200/95 shadow-[0_-2px_8px_rgba(15,23,42,0.04)] px-4 sm:px-6 lg:px-8 py-2 select-none w-full text-slate-800 pb-[calc(0.5rem+env(safe-area-inset-bottom,0px))] md:pb-2 ${
          isDesktopFixedFooterRole
            ? 'hidden md:block shrink-0'
            : 'hidden md:block'
        }`}
      >
        <div className="w-full flex items-center justify-between gap-3 sm:gap-6 min-w-0">
          {/* Left: Active Record Indicator */}
          <div className="flex-1 min-w-0 flex items-center justify-start">
            <div className="flex items-center space-x-2 text-xs shrink-0">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Universal Tools
              </span>
              {selectedClient ? (
                <>
                  <span className="text-slate-300">|</span>
                  <div className="flex items-center space-x-1.5 px-2.5 py-1 rounded-md bg-slate-100 border border-slate-200 text-slate-800 font-medium">
                    <User className="w-3.5 h-3.5 text-slate-600" />
                    <span className="max-w-[200px] truncate">{activeTargetName}</span>
                  </div>
                </>
              ) : null}
            </div>
          </div>

          {/* Center: Action Buttons Cluster */}
          <div className="shrink-0 flex items-center justify-center">
            <div className="flex items-center flex-nowrap overflow-x-auto no-scrollbar py-0.5">
              {/* Main Actions: Call, Fax, Email / Draft, Add Note, Clinical Doc */}
              <div className="flex items-center gap-[16px] shrink-0">
                {/* 1. Call */}
                {canUseUniversalCall && (
                  <button
                    onClick={handleTriggerCall}
                    className="flex items-center space-x-1.5 px-2.5 py-1 rounded-md bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-300 text-xs font-semibold transition-colors shadow-2xs shrink-0 cursor-pointer"
                    title="Launch phone call"
                  >
                    <Phone className="w-3.5 h-3.5 text-emerald-600 transition-colors" />
                    <span className="hidden sm:inline">Call</span>
                  </button>
                )}

                {/* 2. Fax */}
                {canUseUniversalCall && (
                  <button
                    onClick={handleTriggerFax}
                    className="flex items-center space-x-1.5 px-2.5 py-1 rounded-md bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-300 text-xs font-semibold transition-colors shadow-2xs shrink-0 cursor-pointer"
                    title="Transmit or draft electronic fax"
                  >
                    <Printer className="w-3.5 h-3.5 text-slate-600 transition-colors" />
                    <span className="hidden sm:inline">Fax</span>
                  </button>
                )}

                {/* 3. Email / Draft */}
                {!isClinicalStaff && (
                  <button
                    onClick={handleTriggerEmail}
                    className="flex items-center space-x-1.5 px-2.5 py-1 rounded-md bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-300 text-xs font-semibold transition-colors shadow-2xs shrink-0 cursor-pointer"
                    title="Draft email communication"
                  >
                    <Mail className="w-3.5 h-3.5 text-slate-600 transition-colors" />
                    <span className="hidden sm:inline">Email / Draft</span>
                  </button>
                )}

                {/* 4. Add Note */}
                {!isClinicalStaff && (
                  <button
                    onClick={handleTriggerAddNote}
                    className="flex items-center space-x-1.5 px-2.5 py-1 rounded-md bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-300 text-xs font-semibold transition-colors shadow-2xs shrink-0 cursor-pointer"
                    title="Add note / information to record"
                  >
                    <FileEdit className="w-3.5 h-3.5 text-amber-600 transition-colors" />
                    <span className="hidden sm:inline">Add Note</span>
                  </button>
                )}

                {/* 5. Create / Open Clinical Doc */}
                <button
                  onClick={handleTriggerClinicalDoc}
                  className="flex items-center space-x-1.5 px-2.5 py-1 rounded-md bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-300 text-xs font-semibold transition-colors shadow-2xs cursor-pointer shrink-0"
                  title="Create or open clinical document"
                >
                  <FilePlus className="w-3.5 h-3.5 text-slate-600 transition-colors" />
                  <span className="hidden sm:inline">Clinical Doc</span>
                </button>
              </div>

              {/* Separation before Sound toggle: 22px */}
              <div className="h-4 w-px bg-slate-200 hidden sm:block shrink-0 ml-[11px] mr-[10px]" />

              {/* Global Sound Control (Default: OFF) */}
              <button
                onClick={handleToggleSound}
                className="flex items-center space-x-1.5 px-2 py-1 rounded-md bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-300 text-xs font-medium transition-colors shadow-2xs cursor-pointer shrink-0"
                title={soundMode !== 'OFF' ? 'Global sound enabled (Click to turn Sound OFF)' : 'Global sound muted (Click to turn Sound ON)'}
                aria-label={`Sound is ${soundMode !== 'OFF' ? 'ON' : 'OFF'}. Click to toggle.`}
              >
                {soundMode !== 'OFF' ? (
                  <Volume2 className="w-3.5 h-3.5 text-slate-800 shrink-0" />
                ) : (
                  <VolumeX className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                )}
                <span className="font-mono text-[11px] leading-none">
                  Sound {soundMode !== 'OFF' ? 'ON' : 'OFF'}
                </span>
              </button>
            </div>
          </div>

          {/* Right: Remaining status/minimal controls only if needed (Balances left flex-1 for geometric center) */}
          <div className="flex-1 min-w-0 hidden md:flex items-center justify-end" />
        </div>
      </div>

      {/* Ephemeral Feedback Toast */}
      {feedbackMsg && (
        <div className="fixed bottom-14 right-6 z-50 px-3.5 py-2 rounded-lg bg-white border border-slate-200 text-slate-800 text-xs shadow-xl flex items-center space-x-2 animate-in fade-in slide-in-from-bottom-2">
          <AlertCircle className="w-4 h-4 text-slate-600 shrink-0" />
          <span>{feedbackMsg}</span>
        </div>
      )}

      {/* Email Draft Modal */}
      {showDraftEmailModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex flex-col sm:items-center sm:justify-center p-0 sm:p-4 animate-in fade-in duration-150 overflow-y-auto">
          <div className="relative w-full h-full sm:h-auto max-w-full sm:max-w-xl rounded-none sm:rounded-2xl bg-white border-0 sm:border border-slate-200/90 shadow-xl overflow-hidden max-h-full sm:max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-4 sm:px-6 py-3.5 sm:py-4 bg-slate-900 border-b border-slate-800 flex items-center justify-between shrink-0">
              <div className="flex items-center space-x-2 sm:space-x-2.5 min-w-0">
                <button
                  type="button"
                  onClick={handleBackFromEmail}
                  className="sm:hidden p-1.5 -ml-1 text-slate-400 hover:text-white flex items-center space-x-1 rounded-lg"
                  title="Back"
                >
                  <ChevronLeft className="w-5 h-5 text-slate-300" />
                  <span className="text-xs font-semibold">Back</span>
                </button>
                <div className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-200 shrink-0">
                  <Mail className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-bold text-white tracking-tight truncate">Universal Email Transport</h3>
                  <p className="text-[11px] text-slate-300 truncate">Compose and transmit structured correspondence</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCloseEmail}
                className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors shrink-0"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form noValidate onSubmit={handleSendEmail} className="p-4 sm:p-5 space-y-3.5 text-xs overflow-y-auto flex-1">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center justify-between">
                <span className="text-[11px] text-slate-600 font-medium">Sender Transport:</span>
                <span className="text-[11px] font-semibold text-slate-800 font-mono">
                  Allegiant Therapy &lt;info@allegianttherapy.com&gt;
                </span>
              </div>

              {/* Template Selector */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-slate-600 font-semibold flex items-center space-x-1.5">
                    <span>Office Email Template</span>
                    <span className="text-[10px] font-normal text-slate-500 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">Manual Selection Only</span>
                  </label>
                  {emailDraft.templateKey && (
                    <span className="text-[10px] text-slate-600">Prefilled • Review/edit before sending</span>
                  )}
                </div>
                <select
                  value={emailDraft.templateKey}
                  onChange={(e) => applyEmailTemplate(e.target.value)}
                  className="w-full bg-slate-50/70 border border-slate-200 rounded-lg px-3 py-2 sm:py-1.5 text-base sm:text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500 font-medium"
                >
                  <option value="">-- Select a Manual Office Template --</option>
                  <optgroup label="Authorization Expirations (To Support Coordinator)">
                    <option value="auth_7day">7-Day Authorization Expiration</option>
                    <option value="auth_3day">3-Day Authorization Expiration (Urgent)</option>
                  </optgroup>
                  <optgroup label="Plan of Care & Service Coordination">
                    <option value="poc_sc">POC / Support Coordinator Email</option>
                    <option value="support_coordinator">General Support Coordinator Update</option>
                  </optgroup>
                  <optgroup label="Client & Family Communications">
                    <option value="welcome_allegiant">Welcome to Allegiant Therapy</option>
                    <option value="general_client">General Client / Guardian Message</option>
                    <option value="poc_signature">Plan of Care Signature Request (To Guardian)</option>
                  </optgroup>
                  <optgroup label="Physician & Clinical Records">
                    <option value="physician_pcp">Physician / PCP Records Request</option>
                    <option value="doc_delivery">Clinical Document Delivery</option>
                  </optgroup>
                </select>
                <p className="text-[10px] text-slate-500 mt-1">
                  Staff chooses a template manually. Prefills client data and allows full review/edit before sending. No automatic sends.
                </p>
              </div>

              {(() => {
                const isAuthNotice = emailDraft.templateKey === 'auth_7day' || emailDraft.templateKey === 'auth_3day' || Boolean(emailDraft.noticeType);
                const chartSc = verifiedScEmail((activeOperativeClient || selectedClient)?.support_coordinator_email);
                if (!isAuthNotice || chartSc) return null;
                return (
                  <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-[11px] leading-normal">
                    No verified SC email on chart — enter recipient manually
                  </div>
                );
              })()}

              <div>
                <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1">To Email Address</label>
                <input
                  type="email"
                  required
                  value={emailDraft.to}
                  onChange={(e) => setEmailDraft({ ...emailDraft, to: e.target.value })}
                  placeholder="name@organization.com"
                  className="w-full bg-slate-50/70 border border-slate-200 rounded-lg px-3 py-2 sm:py-1.5 text-base sm:text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1">CC (Optional)</label>
                  <input
                    type="text"
                    value={emailDraft.cc}
                    onChange={(e) => setEmailDraft({ ...emailDraft, cc: e.target.value })}
                    placeholder="supervisor@allegianttherapy.com"
                    className="w-full bg-slate-50/70 border border-slate-200 rounded-lg px-3 py-2 sm:py-1.5 text-base sm:text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1">BCC (Optional)</label>
                  <input
                    type="text"
                    value={emailDraft.bcc}
                    onChange={(e) => setEmailDraft({ ...emailDraft, bcc: e.target.value })}
                    placeholder="records@allegianttherapy.com"
                    className="w-full bg-slate-50/70 border border-slate-200 rounded-lg px-3 py-2 sm:py-1.5 text-base sm:text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1">Subject</label>
                <input
                  type="text"
                  required
                  value={emailDraft.subject}
                  onChange={(e) => setEmailDraft({ ...emailDraft, subject: e.target.value })}
                  className="w-full bg-slate-50/70 border border-slate-200 rounded-lg px-3 py-2 sm:py-1.5 text-base sm:text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                />
              </div>

              {/* Attach Existing Completed Clinical Document */}
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1">Attach Existing Document (Optional)</label>
                <select
                  value={emailDraft.attachmentDocId}
                  onChange={(e) => setEmailDraft({ ...emailDraft, attachmentDocId: e.target.value })}
                  className="w-full bg-slate-50/70 border border-slate-200 rounded-lg px-3 py-2 sm:py-1.5 text-base sm:text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                >
                  <option value="">No Document Attached</option>
                  {clientDocs.map(doc => (
                    <option key={doc.id} value={doc.id}>
                      [{doc.doc_type === 'PHYSICIAN_SIGNED_POC' ? 'Speech CPOC' : doc.doc_type === 'PHYSICIAN_SIGNED_FEEDING_POC' ? 'Feeding CPOC' : doc.doc_type}] {doc.title || doc.id} {doc.source ? `(${doc.source === 'INCOMING_FAX' ? 'Fax' : 'Upload'})` : ''}
                    </option>
                  ))}
                </select>
                <span className="text-[10px] text-slate-500 mt-0.5 block">
                  Attaches authentic clinical record directly from chart without duplicating files.
                </span>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1">Message Body</label>
                <textarea
                  rows={5}
                  required
                  value={emailDraft.body}
                  onChange={(e) => setEmailDraft({ ...emailDraft, body: e.target.value })}
                  className="w-full bg-slate-50/70 border border-slate-200 rounded-lg px-3 py-2 sm:py-1.5 text-base sm:text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500 font-sans leading-relaxed"
                />
              </div>

              {emailSendError && (
                <div role="alert" className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-rose-800 text-xs leading-normal">
                  {emailSendError}
                </div>
              )}

              <div className="flex justify-end space-x-2.5 pt-3 border-t border-slate-200 shrink-0 pb-[calc(16px+env(safe-area-inset-bottom,0px))] sm:pb-0">
                <button
                  type="button"
                  onClick={handleCloseEmail}
                  className="px-3.5 py-2 rounded-lg bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sendingEmail}
                  className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold flex items-center space-x-2 shadow-xs transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  <span>{sendingEmail ? 'Transmitting...' : 'Send Email'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Note Modal */}
      {showAddNoteModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex flex-col sm:items-center sm:justify-center p-0 sm:p-4 animate-in fade-in duration-150 overflow-y-auto">
          <div className="relative w-full h-full sm:h-auto max-w-full sm:max-w-md rounded-none sm:rounded-2xl bg-white border-0 sm:border border-slate-200/90 shadow-xl overflow-hidden flex flex-col max-h-full sm:max-h-[90vh]">
            {/* Header */}
            <div className="px-4 sm:px-6 py-3.5 sm:py-4 bg-slate-900 border-b border-slate-800 flex items-center justify-between shrink-0">
              <div className="flex items-center space-x-2 sm:space-x-2.5 min-w-0">
                <button
                  type="button"
                  onClick={handleBackFromNote}
                  className="sm:hidden p-1.5 -ml-1 text-slate-400 hover:text-white flex items-center space-x-1 rounded-lg"
                  title="Back"
                >
                  <ChevronLeft className="w-5 h-5 text-slate-300" />
                  <span className="text-xs font-semibold">Back</span>
                </button>
                <div className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-200 shrink-0">
                  <FileEdit className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-bold text-white tracking-tight truncate">Universal Quick Note</h3>
                  <p className="text-[11px] text-slate-300 truncate">Append timestamped operational chart note</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCloseNote}
                className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors shrink-0"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSaveNote} className="p-4 sm:p-5 space-y-3.5 text-xs flex-1 overflow-y-auto">
              <p className="text-slate-700 font-medium">
                Record an operational note for{' '}
                <strong className="text-slate-900 font-semibold block sm:inline mt-1 sm:mt-0 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-lg break-words">{activeTargetName}</strong>:
              </p>

              {noteError && (
                <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs">
                  {noteError}
                </div>
              )}

              <textarea
                rows={4}
                required
                value={quickNote}
                onChange={(e) => {
                  setQuickNote(e.target.value);
                  if (noteError) setNoteError(null);
                }}
                placeholder="Enter note or operational information..."
                className="w-full bg-slate-50/70 border border-slate-200 rounded-lg p-3 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-sky-500 text-base sm:text-xs"
              />
              <div className="flex justify-end space-x-2.5 pt-3 border-t border-slate-200 shrink-0 pb-[calc(16px+env(safe-area-inset-bottom,0px))] sm:pb-0">
                <button
                  type="button"
                  onClick={handleCloseNote}
                  className="px-3.5 py-2 rounded-lg bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingNote}
                  className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold flex items-center space-x-1.5 disabled:opacity-50 shadow-xs"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>{savingNote ? 'Saving...' : 'Save Note'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Universal Tools Client Selection Modal — Canonical Bloom Light Surface */}
      {showClientPickerModal && (() => {
        // Canonical Bloom Light Shared Picker Canon with restrained semantic tool icon identity
        const toolAccent = {
          CALL: {
            name: 'Universal Call',
            iconColor: 'text-emerald-400',
            iconTile: 'bg-emerald-500/10 border-emerald-500/30'
          },
          FAX: {
            name: 'Universal Fax',
            iconColor: 'text-slate-200',
            iconTile: 'bg-slate-800 border-slate-700'
          },
          EMAIL: {
            name: 'Universal Email / Draft',
            iconColor: 'text-slate-200',
            iconTile: 'bg-slate-800 border-slate-700'
          },
          NOTE: {
            name: 'Quick Note',
            iconColor: 'text-amber-400',
            iconTile: 'bg-amber-500/10 border-amber-500/30'
          },
          CLINICAL_DOC: {
            name: 'Clinical Document',
            iconColor: 'text-teal-400',
            iconTile: 'bg-teal-500/10 border-teal-500/30'
          }
        };

        const accent = pendingTool && toolAccent[pendingTool] ? toolAccent[pendingTool] : toolAccent.FAX;

        return (
          <>
            {/* Desktop / Tablet Client Picker Modal (Bloom Light Surface Canon) */}
            <div className="hidden sm:flex fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs items-center justify-center p-4 animate-in fade-in duration-150">
              <div className="relative w-full max-w-lg rounded-2xl bg-white border border-slate-200/90 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                {/* Specular hairline top reflection */}
                <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-slate-400/20 to-transparent pointer-events-none z-20" />

                {/* Modal Header: Canonical Slate-Blue / Navy Structural Header */}
                <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-900 shrink-0">
                  <div className="flex items-center space-x-3">
                    <div className={`w-9 h-9 rounded-xl border flex items-center justify-center shrink-0 shadow-xs ${accent.iconTile}`}>
                      {pendingTool === 'CALL' && <Phone className={`w-4 h-4 ${accent.iconColor}`} />}
                      {pendingTool === 'FAX' && <Printer className={`w-4 h-4 ${accent.iconColor}`} />}
                      {pendingTool === 'EMAIL' && <Mail className={`w-4 h-4 ${accent.iconColor}`} />}
                      {pendingTool === 'NOTE' && <FileEdit className={`w-4 h-4 ${accent.iconColor}`} />}
                      {pendingTool === 'CLINICAL_DOC' && <FilePlus className={`w-4 h-4 ${accent.iconColor}`} />}
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-white tracking-tight">
                        {pendingTool === 'CALL' && 'Select Client for Universal Call'}
                        {pendingTool === 'FAX' && 'Select Client for Universal Fax'}
                        {pendingTool === 'EMAIL' && 'Select Client for Universal Email / Draft'}
                        {pendingTool === 'NOTE' && 'Select Client for Quick Note'}
                        {pendingTool === 'CLINICAL_DOC' && 'Select Client for Clinical Document'}
                      </h3>
                      <p className="text-[11px] text-slate-300 font-mono mt-0.5">
                        Choose an active chart record to initiate this universal action
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowClientPickerModal(false);
                      setPendingTool(null);
                      setClientPickerSearch('');
                    }}
                    className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
                    title="Cancel"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Search Input: White / Very Light Slate Input with Slate Border */}
                <div className="p-4 border-b border-slate-200/80 bg-slate-50/70 shrink-0">
                  <div className="relative">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3 pointer-events-none transition-colors" />
                    <input
                      type="text"
                      autoFocus
                      value={clientPickerSearch}
                      onChange={(e) => setClientPickerSearch(e.target.value)}
                      placeholder="Filter client by name, AHCCCS ID, or policy..."
                      className="w-full bg-white border border-slate-300/90 rounded-xl pl-10 pr-4 py-2 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-300 shadow-xs transition-all"
                    />
                  </div>
                </div>

                {/* Client List: White / Eggshell Body with Clean Dividers */}
                <div className="max-h-72 overflow-y-auto p-2 divide-y divide-slate-100 bg-white">
                  {loadingClients && pickerClients.length === 0 ? (
                    <div className="py-8 text-center text-xs text-slate-500">Loading client roster...</div>
                  ) : filteredPickerClients.length === 0 ? (
                    <div className="py-8 text-center text-xs text-slate-500">
                      {clientPickerSearch ? `No clients matching "${clientPickerSearch}"` : 'No clients available'}
                    </div>
                  ) : (
                    filteredPickerClients.slice(0, 150).map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        data-client-id={c.id}
                        onClick={() => handleSelectClientForPendingTool(c)}
                        className="w-full text-left p-2.5 rounded-xl border border-transparent hover:border-slate-200/80 hover:bg-slate-50 even:bg-slate-50/40 flex items-center justify-between transition-all duration-150 group my-0.5 cursor-pointer"
                      >
                        <div className="flex items-center space-x-3 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 text-slate-700 group-hover:bg-slate-200/80 flex items-center justify-center text-xs font-semibold shrink-0 transition-all">
                            {c.first_name?.[0] || 'C'}{c.last_name?.[0] || ''}
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs font-semibold text-slate-900 truncate">
                              {c.last_name}, {c.first_name}
                            </div>
                            <div className="text-[10px] text-slate-500 flex items-center space-x-2 mt-0.5 font-mono">
                              {c.ahcccs_id && <span>AHCCCS: <span className="font-mono text-slate-700 font-medium">{c.ahcccs_id}</span></span>}
                              {c.policy_id && <span>ID: <span className="font-mono text-slate-700 font-medium">{c.policy_id}</span></span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1.5 px-2.5 py-1 rounded-lg border border-slate-700/80 bg-slate-800 hover:bg-slate-900 text-white text-xs font-medium shrink-0 ml-2 shadow-xs transition-all duration-150">
                          <span className="text-[11px] font-semibold">Select</span>
                          <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
                        </div>
                      </button>
                    ))
                  )}
                </div>

                {/* Footer: Clean Light Neutral Footer with Neutral Cancel Control */}
                <div className="px-6 py-3 border-t border-slate-200/80 flex justify-end shrink-0 bg-slate-50/90">
                  <button
                    type="button"
                    onClick={() => {
                      setShowClientPickerModal(false);
                      setPendingTool(null);
                      setClientPickerSearch('');
                    }}
                    className="px-4 py-1.5 rounded-xl text-xs border border-slate-300/80 bg-white hover:bg-slate-100 text-slate-700 font-medium transition-all duration-150 cursor-pointer shadow-xs"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile Phone Native Client Picker Surface (< 640px) */}
            <div className="sm:hidden fixed inset-0 z-50 bg-[#f8fafc] flex flex-col animate-in fade-in duration-150 text-slate-900">
              {/* Header: Canonical Slate-Blue / Navy Structural Header */}
              <div className="px-4 py-3.5 bg-gradient-to-r from-[#2a394d] via-[#1a2a42] to-[#0d1727] border-b border-slate-700/80 flex items-center justify-between shrink-0">
                <button
                  type="button"
                  onClick={handleBackFromPicker}
                  className="flex items-center space-x-1 text-slate-300 active:text-white p-1 -ml-1 rounded-lg hover:bg-white/10 transition-colors"
                  title="Back to Universal Tools"
                >
                  <ChevronLeft className="w-5 h-5 text-slate-300" />
                  <span className="text-xs font-semibold">Back</span>
                </button>
                <div className="text-center">
                  <h3 className="text-sm font-bold text-white tracking-tight">Select Client</h3>
                  <p className="text-[10px] text-slate-300 font-mono">
                    {pendingTool === 'CALL' ? 'Universal Call' :
                     pendingTool === 'FAX' ? 'Universal Fax' :
                     pendingTool === 'EMAIL' ? 'Email / Draft' :
                     pendingTool === 'NOTE' ? 'Add Note' :
                     pendingTool === 'CLINICAL_DOC' ? 'Clinical Doc' : 'Universal Tools'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleClosePicker}
                  className="p-1.5 text-slate-300 active:text-white rounded-lg hover:bg-white/10 transition-colors"
                  title="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Search bar: Clean light search well */}
              <div className="p-3 bg-white border-b border-slate-200/80 shrink-0">
                <div className="relative">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
                  <input
                    type="text"
                    value={clientPickerSearch}
                    onChange={(e) => setClientPickerSearch(e.target.value)}
                    placeholder="Search clients..."
                    className="w-full bg-slate-50 border border-slate-300/80 rounded-xl pl-9 pr-8 py-2 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-slate-500"
                  />
                  {clientPickerSearch && (
                    <button
                      type="button"
                      onClick={() => setClientPickerSearch('')}
                      className="absolute right-2.5 top-2 p-1 text-slate-400 hover:text-slate-600"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Mobile Client List: Clean eggshell surface, light cards */}
              <div className="flex-1 overflow-y-auto px-3 py-2 bg-[#f8fafc] pb-[calc(24px+env(safe-area-inset-bottom,0px))] space-y-1.5">
                {loadingClients && pickerClients.length === 0 ? (
                  <div className="py-8 text-center text-xs text-slate-500">Loading client roster...</div>
                ) : filteredPickerClients.length === 0 ? (
                  <div className="py-8 text-center text-xs text-slate-500">
                    {clientPickerSearch ? `No clients matching "${clientPickerSearch}"` : 'No clients available'}
                  </div>
                ) : (
                  filteredPickerClients.slice(0, 150).map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      data-client-id={c.id}
                      onClick={() => handleSelectClientForPendingTool(c)}
                      className="w-full text-left py-3 px-3 rounded-xl bg-white border border-slate-200/70 hover:bg-slate-50 active:bg-slate-100 flex items-center justify-between transition-colors cursor-pointer shadow-xs"
                    >
                      <div className="min-w-0 flex-1 pr-3">
                        <div className="text-sm font-semibold text-slate-900 truncate">
                          {c.last_name}, {c.first_name}
                        </div>
                        {c.ahcccs_id && (
                          <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                            AHCCCS: <span className="text-slate-700 font-medium">{c.ahcccs_id}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-1 px-2.5 py-1 rounded-lg bg-slate-800 text-white text-xs font-medium shrink-0 shadow-xs">
                        <span>Select</span>
                        <ChevronRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </>
        );
      })()}

      {/* ========================================================================= */}
      {/* MOBILE COMPACT UNIVERSAL TOOLS BOTTOM SHEET / DRAWER (Phone Only)         */}
      {/* ========================================================================= */}
      {isMobileOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs md:hidden animate-in fade-in duration-150 flex flex-col justify-end">
          {/* Backdrop click to dismiss */}
          <div
            className="flex-1 w-full"
            onClick={handleCloseMobileSheet}
          />
          <div
            id="pulse-mobile-universal-tools-sheet"
            className="relative w-full rounded-t-2xl border-t border-x bg-white border-slate-200/90 shadow-[0_-8px_30px_rgba(15,23,42,0.12)] p-4 space-y-3.5 animate-in slide-in-from-bottom-4 duration-200 select-none pb-[calc(18px+env(safe-area-inset-bottom,0px))] max-h-[85vh] overflow-y-auto text-slate-900"
          >
            {/* Top Structural Header: Deep slate/navy Bloom structural header */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-[#2a394d] via-[#1a2a42] to-[#0d1727] border border-slate-700/80 shadow-sm text-white">
              <div className="flex items-center space-x-2.5">
                <div className="w-8 h-8 rounded-xl border flex items-center justify-center bg-slate-800/90 border-slate-600/70 text-sky-300 shadow-xs">
                  <Wrench className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-xs font-bold text-white tracking-tight flex items-center gap-1.5">
                    <span>Universal Tools</span>
                    <span className="text-[9px] font-mono px-1.5 py-0.2 rounded border uppercase tracking-wider font-semibold bg-slate-800/90 text-slate-200 border-slate-600/70">
                      Suite
                    </span>
                  </h3>
                  <p className="text-[10.5px] text-slate-300 mt-0.5">
                    Quick client actions &amp; clinical utility transport
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCloseMobileSheet}
                className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 active:bg-white/20 transition-colors cursor-pointer"
                title="Close Universal Tools"
                aria-label="Close Universal Tools"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Universal AI & Search Entry (Bloom Light Canon: light surface, dark text, restrained cyan/blue accent, no glow) */}
            <button
              id="mobile-hub-ai-search"
              type="button"
              onClick={() => {
                handleCloseMobileSheet();
                handleTriggerAI();
              }}
              className="w-full relative overflow-hidden flex items-center justify-between p-2.5 rounded-xl bg-slate-50 hover:bg-slate-100/90 border border-slate-200/90 hover:border-slate-300 text-left transition-all active:scale-[0.99] cursor-pointer shadow-xs group"
            >
              <div className="flex items-center space-x-2.5 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-sky-50 border border-sky-200 flex items-center justify-center shrink-0 shadow-xs">
                  <img src="/brand/canonical/bloom-mark-micro.svg" alt="Bloom" className="w-5 h-5 shrink-0 object-contain" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 leading-none">
                    <span className="text-xs font-bold text-slate-900 tracking-tight">
                      Universal AI &amp; Search
                    </span>
                    <span className="text-[9px] font-mono font-bold px-1.5 py-0.2 rounded bg-sky-100 text-sky-800 border border-sky-200/80 leading-none">
                      BLOOM
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1 truncate">
                    Caseload queries, authorizations, clinical records &amp; smart actions
                  </p>
                </div>
              </div>
              <div className="flex items-center pl-2 shrink-0">
                <span className="p-1 rounded-md bg-sky-50 text-sky-700 group-hover:bg-sky-100 group-hover:text-sky-800 transition-colors">
                  <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </button>

            <p className="text-[11px] text-slate-600 px-0.5">
              {selectedClient
                ? `Chart context: ${selectedClient.last_name}, ${selectedClient.first_name}`
                : 'Choose a client when you start an action.'}
            </p>

            {/* Action Buttons: Clean Wrapped Grid (3x2) — Bloom Light Canon */}
            <div className="grid grid-cols-3 gap-2 pt-0.5">
              {/* 1. Call */}
              {canUseUniversalCall && (
                <button
                  id="mobile-tool-btn-call"
                  type="button"
                  onClick={() => {
                    handleCloseMobileSheet();
                    setMobileWorkflowOrigin('HUB');
                    handleTriggerCall();
                  }}
                  className="flex flex-col items-center justify-center p-2.5 rounded-xl bg-white hover:bg-slate-50 active:bg-slate-100 border border-slate-200/90 hover:border-slate-300 text-slate-800 transition-all active:scale-95 group cursor-pointer shadow-xs min-h-[62px]"
                >
                  <Phone className="w-5 h-5 text-emerald-600 group-hover:text-emerald-700 transition-colors mb-1 shrink-0" />
                  <span className="text-[11px] font-semibold text-slate-800 group-hover:text-slate-950 tracking-tight whitespace-nowrap">
                    Call
                  </span>
                </button>
              )}

              {/* 2. Fax */}
              {canUseUniversalCall && (
              <button
                id="mobile-tool-btn-fax"
                type="button"
                onClick={() => {
                  handleCloseMobileSheet();
                  setMobileWorkflowOrigin('HUB');
                  handleTriggerFax();
                }}
                className="flex flex-col items-center justify-center p-2.5 rounded-xl bg-white hover:bg-slate-50 active:bg-slate-100 border border-slate-200/90 hover:border-slate-300 text-slate-800 transition-all active:scale-95 group cursor-pointer shadow-xs min-h-[62px]"
              >
                <Printer className="w-5 h-5 text-sky-600 group-hover:text-sky-700 transition-colors mb-1 shrink-0" />
                <span className="text-[11px] font-semibold text-slate-800 group-hover:text-slate-950 tracking-tight whitespace-nowrap">
                  Fax
                </span>
              </button>
              )}

              {/* 3. Email / Draft */}
              <button
                id="mobile-tool-btn-email"
                type="button"
                onClick={() => {
                  handleCloseMobileSheet();
                  setMobileWorkflowOrigin('HUB');
                  handleTriggerEmail();
                }}
                className="flex flex-col items-center justify-center p-2.5 rounded-xl bg-white hover:bg-slate-50 active:bg-slate-100 border border-slate-200/90 hover:border-slate-300 text-slate-800 transition-all active:scale-95 group cursor-pointer shadow-xs min-h-[62px]"
              >
                <Mail className="w-5 h-5 text-indigo-600 group-hover:text-indigo-700 transition-colors mb-1 shrink-0" />
                <span className="text-[11px] font-semibold text-slate-800 group-hover:text-slate-950 tracking-tight whitespace-nowrap">
                  Email / Draft
                </span>
              </button>

              {/* 4. Add Note */}
              <button
                id="mobile-tool-btn-note"
                type="button"
                onClick={() => {
                  handleCloseMobileSheet();
                  setMobileWorkflowOrigin('HUB');
                  handleTriggerAddNote();
                }}
                className="flex flex-col items-center justify-center p-2.5 rounded-xl bg-white hover:bg-slate-50 active:bg-slate-100 border border-slate-200/90 hover:border-slate-300 text-slate-800 transition-all active:scale-95 group cursor-pointer shadow-xs min-h-[62px]"
              >
                <FileText className="w-5 h-5 text-amber-600 group-hover:text-amber-700 transition-colors mb-1 shrink-0" />
                <span className="text-[11px] font-semibold text-slate-800 group-hover:text-slate-950 tracking-tight whitespace-nowrap">
                  Add Note
                </span>
              </button>

              {/* 5. Clinical Doc */}
              <button
                id="mobile-tool-btn-doc"
                type="button"
                onClick={() => {
                  handleCloseMobileSheet();
                  setMobileWorkflowOrigin('HUB');
                  handleTriggerClinicalDoc();
                }}
                className="flex flex-col items-center justify-center p-2.5 rounded-xl bg-white hover:bg-slate-50 active:bg-slate-100 border border-slate-200/90 hover:border-slate-300 text-slate-800 transition-all active:scale-95 group cursor-pointer shadow-xs min-h-[62px]"
              >
                <FilePlus className="w-5 h-5 text-teal-600 group-hover:text-teal-700 transition-colors mb-1 shrink-0" />
                <span className="text-[11px] font-semibold text-slate-800 group-hover:text-slate-950 tracking-tight whitespace-nowrap">
                  Clinical Doc
                </span>
              </button>

              {/* 6. Sound */}
              <button
                id="mobile-tool-btn-sound"
                type="button"
                onClick={handleToggleSound}
                className={`flex flex-col items-center justify-center p-2.5 rounded-xl border transition-all active:scale-95 group cursor-pointer shadow-xs min-h-[62px] ${
                  soundMode !== 'OFF'
                    ? 'bg-sky-50 border-sky-300 text-sky-900 hover:bg-sky-100/80'
                    : 'bg-white hover:bg-slate-50 active:bg-slate-100 border-slate-200/90 hover:border-slate-300 text-slate-800'
                }`}
              >
                {soundMode !== 'OFF' ? (
                  <Volume2 className="w-5 h-5 text-sky-600 group-hover:text-sky-700 transition-colors mb-1 shrink-0" />
                ) : (
                  <VolumeX className="w-5 h-5 text-slate-400 group-hover:text-slate-600 transition-colors mb-1 shrink-0" />
                )}
                <span className={`text-[11px] font-semibold tracking-tight font-mono whitespace-nowrap ${
                  soundMode !== 'OFF' ? 'text-sky-900' : 'text-slate-800 group-hover:text-slate-950'
                }`}>
                  Sound {soundMode !== 'OFF' ? 'ON' : 'OFF'}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
